import DataStructure, { sanitize } from "./DataStructure.js";
import { deepFreeze, timestampToDate } from "../util/misc.js";
import { every, get, isEmpty as _isEmpty } from "lodash";
import { diff } from "deep-object-diff";

export const MILEAGE_LOG_FIELDS = {
  userID: null,
  date: null,
  description: "",
  miles: 0,
  mapLink: "",

  // Entry mode: 'manual' or 'route'
  entryMode: "manual",

  // Route information (for route mode)
  startLocation: null, // { type: 'office'|'custom', value: string, address: string, lat: number, lng: number }
  endLocation: null, // { type: 'office'|'custom', value: string, address: string, lat: number, lng: number }

  // Status: 'unpaid' | 'submitted' | 'archived'
  status: "unpaid",
  expenseID: null,

  // Month/year for easier querying
  month: null, // 1-12
  year: null, // YYYY

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(MILEAGE_LOG_FIELDS);

export const MILEAGE_LOG_STATUSES = {
  unpaid: {
    label: "Unpaid",
    color: "warning",
    icon: "unpaid",
  },
  submitted: {
    label: "Submitted",
    color: "info",
    icon: "pending",
  },
  archived: {
    label: "Archived",
    color: "positive",
    icon: "archive",
  },
};

export default class MileageLog extends DataStructure {
  constructor(id, data = {}) {
    data = sanitize(data, MILEAGE_LOG_FIELDS);

    // Convert to Date
    data.date = timestampToDate(data.date);
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);

    // Ensure month/year are set from date
    if (data.date && !data.month) {
      data.month = data.date.getMonth() + 1;
    }
    if (data.date && !data.year) {
      data.year = data.date.getFullYear();
    }

    super(data);

    this.id = id || null;
    this.permissions = Object.freeze({
      view: MileageLog.canBeViewedBy,
      create: MileageLog.canBeCreatedBy,
      edit: MileageLog.canBeEditedBy,
      delete: MileageLog.canBeDeletedBy,
    });
  }

  // Permissions

  static canBeViewedBy(user, doc) {
    if (user.isAdmin) return true;
    if (doc && doc.data.userID === user.id) return true;
    return false;
  }

  static canBeCreatedBy(user) {
    return user.isEmployee;
  }

  static canBeEditedBy(user, doc) {
    if (user.isAdmin) return true;
    if (doc && doc.data.userID === user.id) return true;
    return false;
  }

  static canBeDeletedBy(user, doc) {
    if (user.isAdmin) return true;
    if (doc && doc.data.userID === user.id) return true;
    return false;
  }

  // Database

  get path() {
    return MileageLog.getPath(this.id);
  }

  static getPath(id) {
    if (id) {
      return `mileageLogs/${id}`;
    }
    return "mileageLogs";
  }

  static getStateID(path) {
    const match = path.match(/^mileageLogs\/([^/]+)$/);
    if (match) {
      return match[1];
    }
    return null;
  }

  get stateID() {
    return this.id;
  }

  // Editing

  get rules() {
    return {
      date: [(a) => Boolean(a) || "required"],
      description: [(a) => Boolean(a && a.trim().length) || "required"],
      miles: [(a) => a > 0 || "milesPositive"],
    };
  }

  get isValid() {
    return every(this.rules, (rules, key) =>
      rules.every((rule) => rule(get(this.data, key)) === true)
    );
  }

  get isEmpty() {
    return Boolean(!this.id && _isEmpty(diff(this.data, MILEAGE_LOG_FIELDS)));
  }

  isEqual(mileageLog) {
    return Boolean(
      this.id === mileageLog.id && _isEmpty(diff(this.data, mileageLog.data))
    );
  }

  toFirestore() {
    return {
      ...this.data,
    };
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new MileageLog(snapshot.id, data);
  }
}
